<!--
  * Path: packages/frontend/src/core/components/common/EntityPanel.vue
  * Task: Renders a uniform visual card representing a single entity with title.
  * Update: Context-aware click routing (Pane-right vs. Tertiary Modal).
-->
<template>
  <v-card
    :class="['entity-panel mb-2 rounded-lg overflow-hidden', { 'no-border': noBorder }]"
    variant="flat"
    color="transparent"
    @click="handleCardClick"
  >
    <!-- Reduced padding to py-1 and enforced a 40px min-height for a sleeker profile -->
    <div class="d-flex align-center px-2 py-1" :style="{ cursor: isLink ? 'pointer' : 'default' }" style="min-width: 0; width: 100%; min-height: 40px;">
      <!-- Reduced Icon Size from 32 to 28 -->
      <EntityIcon
        :entity="entity"
        size="28"
        class="me-3 flex-shrink-0"
      />
      <div class="flex-grow-1 min-w-0 d-flex align-center justify-space-between" style="min-width: 0;">
        <v-tooltip location="top" open-delay="400">
          <template v-slot:activator="{ props: textTooltipProps }">
            <span
              v-bind="textTooltipProps"
              :class="[titleClass, 'text-truncate pr-2']"
              style="display: block; min-width: 0; flex-grow: 1; flex-shrink: 1;"
            >
              {{ entity.title || 'Untitled Element' }}
            </span>
          </template>
          <span>{{ entity.title || 'Untitled Element' }}</span>
        </v-tooltip>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { FormContextKey } from '@/core/keys';
import type { Entity } from '@/core/types/editor';
import EntityIcon from './EntityIcon.vue';

const {
  entity,
  isLink = false,
  inRightPane = false,
  // Changed default typography from text-subtitle-1 to text-body-2 for a more compact look
  titleClass = 'text-body-2 font-weight-medium',
  noBorder = false
} = defineProps<{
  entity: Partial<Entity> & { subtitle?: string };
  isLink?: boolean;
  inRightPane?: boolean;
  titleClass?: string;
  noBorder?: boolean;
}>();

const emit = defineEmits<{
  (e: 'click', event: Event): void;
}>();

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const formContext = inject(FormContextKey, null);

const isSecondaryContext = computed<boolean>(() => inRightPane || (formContext ? formContext.isSecondary : false));

const handleCardClick = (e: Event): void => {
  if (isLink && entity.id) {
    e.stopPropagation();

    // Check if we are ALREADY in a secondary panel context (which includes Modals like Kanban/Calendar)
    if (isSecondaryContext.value) {
      workspaceStore.setPreviewEntity(entity.id);
    } else {
      workspaceStore.setSecondarySelectedEntity(entity.id);
      uiStore.panels.showSecondaryDetails = true;
    }
  }
  emit('click', e);
};
</script>

<style scoped>
.entity-panel {
  position: relative;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: background-color 0.25s ease-in-out, box-shadow 0.25s ease-in-out;
  width: 100%;
}

.entity-panel.no-border {
  border: none !important;
  box-shadow: none !important;
  background-color: transparent !important;
}

.entity-panel:hover:not(.no-border) {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important;
  border-color: rgba(var(--v-theme-on-surface), 0.3) !important;
  background-color: rgba(var(--v-theme-on-surface), 0.03) !important;
}
</style>
